import {
  Banknote,
  BriefcaseBusiness,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Mail,
  MessageSquareText,
  Phone,
  ShieldCheck,
  Sparkles,
  TimerReset,
  UserRound,
  Wrench
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { isTenantRouteScope } from '@/routes/routeScope';

export type ServiceBusinessOrderStatus =
  | 'new'
  | 'scheduled'
  | 'in_progress'
  | 'ready'
  | (string & Record<never, never>);

export interface ServiceBusinessService {
  id?: string;
  name: string;
  description: string;
  price: string;
  duration: string;
  icon: LucideIcon;
  basePrice?: number;
  durationMinutes?: number;
  isActive?: boolean;
  sortOrder?: number;
  checklistItems?: string[];
}

export interface ServiceBusinessOrderChecklistItem {
  id: string;
  label: string;
  isCompleted: boolean;
  completedAt?: string;
}

export interface ServiceBusinessOrder {
  id: string;
  number: string;
  customerId?: string;
  customer: string;
  phone: string;
  subject: string;
  service: string;
  serviceId?: string;
  date: string;
  scheduledAt?: string;
  value: number;
  status: ServiceBusinessOrderStatus;
  priorityKey?: string;
  note: string;
  employeeId?: string;
  employeeIds?: string[];
  employeeName?: string;
  employeeNames?: string[];
  tags?: string[];
  tagIds?: string[];
  followersCount?: number;
  isFollowedByCurrentUser?: boolean;
  checklistItems?: ServiceBusinessOrderChecklistItem[];
}

export interface ServiceBusinessCustomer {
  id?: string;
  name: string;
  phone: string;
  email: string;
  visits: number;
  totalValue: number;
  lastService: string;
  hasPortalAccount?: boolean;
  portalUserId?: string;
  portalInvitationSentAt?: string;
  followersCount?: number;
  isFollowedByCurrentUser?: boolean;
}

const appServiceBusinessPaths = {
  publicBasePath: '/company',
  backofficeBasePath: '/panel'
};

export const serviceBusinessPaths = {
  get publicBasePath() {
    return isTenantRouteScope() ? '/' : appServiceBusinessPaths.publicBasePath;
  },
  get backofficeBasePath() {
    return isTenantRouteScope()
      ? '/admin'
      : appServiceBusinessPaths.backofficeBasePath;
  },
  tenantPublicBasePath: '/',
  tenantBackofficeBasePath: '/admin'
};

export const serviceBusinessLabels = {
  applicationTitle: 'HS Desk | Panel uslug',
  publicPageTitle: 'HS Desk | Wizytowka uslug',
  businessName: 'Firma uslugowa',
  subject: 'Temat zlecenia',
  subjectPlaceholder: 'Np. sprzatanie biura, poprawka sukienki, pranie kanapy',
  publicSubjectPlaceholder: 'Czego dotyczy zlecenie?',
  requestDetailsPlaceholder:
    'Opisz czego potrzebujesz, preferowany zakres i dodatkowe informacje.',
  defaultServiceName: 'Zapytanie z formularza',
  defaultReminderSubject: 'planowanego zlecenia',
  defaultBlogAuthor: 'Firma uslugowa',
  defaultBlogCategory: 'Poradnik'
};

export const defaultCompanyProfile = {
  name: 'Firma Uslugowa',
  city: 'Krakow',
  phone: '+48 600 300 200',
  email: 'kontakt@firma-uslugowa.pl',
  address: 'ul. Przemyslowa 12, Krakow',
  tagline: 'Profesjonalne uslugi lokalne z szybka wycena online.',
  description:
    'Przyjmujemy zapytania z publicznej wizytowki, prowadzimy historie klienta i wysylamy powiadomienia o postepie realizacji.',
  workingHours: 'Pon-Pt 8:00-18:00, Sob 9:00-14:00'
};

export const defaultServices: ServiceBusinessService[] = [
  {
    name: 'Usluga podstawowa',
    description: 'Standardowy zakres pracy dopasowany do potrzeb klienta.',
    price: 'od 249 zl',
    duration: '2-3 h',
    icon: Sparkles
  },
  {
    name: 'Usluga rozszerzona',
    description: 'Szerszy zakres z przygotowaniem, realizacja i kontrola.',
    price: 'od 899 zl',
    duration: '1 dzien',
    icon: Wrench
  },
  {
    name: 'Pakiet premium',
    description:
      'Kompleksowa realizacja z konsultacja, dokumentacja i obsluga po wykonaniu.',
    price: 'od 1699 zl',
    duration: '2 dni',
    icon: ShieldCheck
  }
];

export const defaultOrders: ServiceBusinessOrder[] = [];

export const defaultCustomers: ServiceBusinessCustomer[] = [];

export const defaultHighlights = [
  {
    label: 'Zapytania z formularza',
    value: '18',
    description: 'w tym miesiacu',
    icon: Mail
  },
  {
    label: 'Aktywne zlecenia',
    value: '7',
    description: 'na ten tydzien',
    icon: ClipboardList
  },
  {
    label: 'Wiadomosci SMS',
    value: '42',
    description: 'wyslane automatycznie',
    icon: MessageSquareText
  },
  {
    label: 'Przychod w zleceniach',
    value: '12 480 zl',
    description: 'z otwartych prac',
    icon: Banknote
  }
];

export const defaultWorkflow = [
  {
    label: 'Klient wysyla formularz',
    icon: UserRound
  },
  {
    label: 'Zlecenie trafia do panelu',
    icon: Clock3
  },
  {
    label: 'Status zmienia sie w trakcie pracy',
    icon: TimerReset
  },
  {
    label: 'Klient dostaje powiadomienie',
    icon: CheckCircle2
  }
];

export const contactActions = [
  {
    label: defaultCompanyProfile.phone,
    icon: Phone
  },
  {
    label: defaultCompanyProfile.email,
    icon: Mail
  }
];

export const activeServiceBusinessProfile = {
  paths: serviceBusinessPaths,
  labels: serviceBusinessLabels,
  company: defaultCompanyProfile,
  services: defaultServices,
  orders: defaultOrders,
  customers: defaultCustomers,
  highlights: defaultHighlights,
  workflow: defaultWorkflow,
  contactActions,
  icon: BriefcaseBusiness
};
